/**
 * 全系统统一的班级/年级排序工具（与后端 AcademicOrderService 同算法）。
 *
 * 排序口径（单一规则，所有环节共用）：
 * 1. 年级全局顺序号（gradeOrder，后端系统按「学段配置顺序→学段内年级号」维护，
 *    混合学段学校保证 一→六、初一→初三、高一→高三 连续）升序，无值排末尾
 * 2. 班级编号（从班级名称正则提取最后一个数字）升序，无数字排末尾
 * 3. 班级名称字典序兜底（数字感知、大小写不敏感）
 *
 * 设计目标：消除各模块各自实现的排序漂移，保证课表查询、创建排课任务、
 * 课表详情、Excel 导出、数据管理列表等环节顺序完全一致。
 */

const MAX_ORDER = Number.MAX_SAFE_INTEGER

/**
 * 从班级名称中提取班级编号：取最后一个数字片段，兼容
 * 「二年级(1)班」「二年级1班」「二年级1班(A组)」等格式；
 * 提取不到时返回 {@code Number.MAX_SAFE_INTEGER} 排到末尾。
 */
export function extractClassNumber(name: string | null | undefined): number {
  if (!name || typeof name !== 'string') {
    return MAX_ORDER
  }
  const matches = name.match(/\d+/g)
  if (!matches || matches.length === 0) {
    return MAX_ORDER
  }
  const parsed = Number.parseInt(matches[matches.length - 1], 10)
  return Number.isFinite(parsed) ? parsed : MAX_ORDER
}

/**
 * 班级名称比较（数字感知，无年级维度）。
 * 用于同年级内的班级排序，或作为无年级信息时的兜底比较。
 */
export function compareClassNames(left: string, right: string): number {
  const leftNum = extractClassNumber(left)
  const rightNum = extractClassNumber(right)
  if (leftNum !== rightNum) {
    return leftNum - rightNum
  }
  return (left ?? '').localeCompare(right ?? '', 'zh-CN', { numeric: true, sensitivity: 'base' })
}

function resolveGradeOrder(
  gradeId: number | null | undefined,
  gradeOrderMap?: Map<number, number>
): number {
  if (!gradeId || !gradeOrderMap) {
    return MAX_ORDER
  }
  const order = gradeOrderMap.get(gradeId)
  return order == null ? MAX_ORDER : order
}

/**
 * 通用班级排序：全局年级顺序号 -> 班级编号 -> 名称。
 *
 * @param classes        待排序数组（不会原地修改，返回新数组）
 * @param gradeOrderMap  gradeId -> 全局年级顺序号 映射（无则退化为纯名称+班级号排序）
 * @param gradeIdGetter  从元素取 gradeId，默认取 (item) => item.gradeId
 * @param nameGetter     从元素取名称，默认取 (item) => item.name
 */
export function sortClassOptions<T>(
  classes: T[],
  gradeOrderMap?: Map<number, number>,
  gradeIdGetter?: (item: T) => number | null | undefined,
  nameGetter?: (item: T) => string
): T[] {
  if (!classes || classes.length === 0) {
    return []
  }
  const getGradeId = gradeIdGetter ?? ((item: T) => (item as { gradeId?: number }).gradeId)
  const getName = nameGetter ?? ((item: T) => (item as { name?: string }).name ?? '')

  return [...classes].sort((left, right) => {
    const leftOrder = resolveGradeOrder(getGradeId(left), gradeOrderMap)
    const rightOrder = resolveGradeOrder(getGradeId(right), gradeOrderMap)
    if (leftOrder !== rightOrder) {
      return leftOrder - rightOrder
    }
    return compareClassNames(getName(left) ?? '', getName(right) ?? '')
  })
}

/**
 * 通用年级排序：全局年级顺序号 -> 年级名称(兜底)。
 *
 * @param grades        待排序数组
 * @param gradeOrderMap gradeId -> 全局年级顺序号 映射
 * @param idGetter      从元素取 gradeId
 * @param nameGetter    从元素取名称
 */
export function sortGradeOptions<T>(
  grades: T[],
  gradeOrderMap: Map<number, number>,
  idGetter: (item: T) => number | null | undefined,
  nameGetter: (item: T) => string
): T[] {
  if (!grades || grades.length === 0) {
    return []
  }
  return [...grades].sort((left, right) => {
    const leftOrder = resolveGradeOrder(idGetter(left), gradeOrderMap)
    const rightOrder = resolveGradeOrder(idGetter(right), gradeOrderMap)
    if (leftOrder !== rightOrder) {
      return leftOrder - rightOrder
    }
    return (nameGetter(left) ?? '').localeCompare(nameGetter(right) ?? '', 'zh-CN', {
      numeric: true,
      sensitivity: 'base'
    })
  })
}

/**
 * 从任意带 gradeId + 全局年级顺序号（gradeOrder）的列表聚合出 gradeOrderMap。
 * 例如从后端返回的年级列表（含 id 与 sort）或班级列表（含 gradeId 与 gradeOrder）构建。
 */
export function buildGradeOrderMapFromGrades<T>(
  grades: T[],
  idGetter: (g: T) => number | null | undefined,
  orderGetter: (g: T) => number | null | undefined
): Map<number, number> {
  const map = new Map<number, number>()
  if (!grades) {
    return map
  }
  for (const grade of grades) {
    const id = idGetter(grade)
    const order = orderGetter(grade)
    if (id != null) {
      map.set(id, order == null ? MAX_ORDER : order)
    }
  }
  return map
}

/**
 * 从班级列表（含 gradeId + gradeOrder）聚合出 gradeOrderMap。
 * 适用于 ClassDto[] 等已携带 gradeOrder 的场景。
 */
export function buildGradeOrderMapFromClasses<T>(
  classes: T[],
  gradeIdGetter: (item: T) => number | null | undefined,
  gradeOrderGetter: (item: T) => number | null | undefined
): Map<number, number> {
  return buildGradeOrderMapFromGrades(classes, gradeIdGetter, gradeOrderGetter)
}

/**
 * 排课规则树专用排序：对「只含 classId + className、不含 gradeId」的节点列表
 * 按统一口径（全局年级顺序号升序 → 班级编号升序 → 名称字典序）排序。
 *
 * 通过外部 classCatalog 反查每个 classId 对应的 gradeId / gradeOrder，
 * 再委托 {@link sortClassOptions} 完成实际排序，算法与全系统完全一致。
 *
 * 容错策略（保证任何场景都不漂移）：
 * - classId 在 catalog 中命中 → 走完整全局顺序号 + 班级号排序
 * - classId 未命中（编辑模式 catalog 暂未加载、或孤儿班级）→ gradeId 视为 0，
 *   在 gradeOrderMap 中查不到则记为 MAX_ORDER，落入末尾区；
 *   末尾区内仍按 compareClassNames（提取数字 + 字典序）排好，不会乱序
 *
 * @param classes       待排序节点（如排课规则的 CourseClassOption[]）
 * @param classCatalog  班级目录（含 id / gradeId / gradeOrder），通常来自 classCatalog
 * @param classIdGetter 从节点取 classId，默认 (item) => item.classId
 * @param nameGetter    从节点取名称，默认 (item) => item.className
 */
export function sortCourseClassOptions<T>(
  classes: T[],
  classCatalog: ReadonlyArray<{ id: number; gradeId?: number | null; gradeOrder?: number | null }>,
  classIdGetter?: (item: T) => number | null | undefined,
  nameGetter?: (item: T) => string | null | undefined
): T[] {
  if (!classes || classes.length === 0) {
    return []
  }
  const getId = classIdGetter ?? ((item: T) => (item as { classId?: number }).classId)
  const getName = nameGetter ?? ((item: T) => (item as { className?: string }).className ?? '')

  // classId -> gradeId 反查表；同时聚合出 gradeOrderMap（gradeId -> 全局年级顺序号）
  const classIdToGradeId = new Map<number, number>()
  const gradeOrderMap = new Map<number, number>()
  for (const cls of classCatalog ?? []) {
    if (cls == null || cls.id == null) continue
    const gradeId = cls.gradeId ?? 0
    classIdToGradeId.set(cls.id, gradeId)
    if (!gradeOrderMap.has(gradeId)) {
      gradeOrderMap.set(gradeId, cls.gradeOrder == null ? MAX_ORDER : cls.gradeOrder)
    }
  }

  return sortClassOptions(
    classes,
    gradeOrderMap,
    (item) => {
      const classId = getId(item)
      return classId == null ? 0 : (classIdToGradeId.get(classId) ?? 0)
    },
    (item) => getName(item) ?? ''
  )
}
